/**
 * Custom Product Information (Sanne ATF).
 *
 * - Info panel drawer: opens/closes via the `cpi-info-open` body class
 *   (megamenu-style), from `[data-cpi-open]`; dismisses on `[data-cpi-close]`,
 *   overlay click and Escape. Returns focus to the opener on close.
 * - Client-side variant selection: matches the checked options against the
 *   embedded variant data and updates the hidden `id` input (so the theme's
 *   product-form-component adds the right variant with no reload), the price,
 *   the active media, and the add-to-bag label/availability.
 *
 * Swiper media carousel, the four media treatments, zoom and the size guide
 * modal are layered on in later tasks.
 */
const BODY_CLASS = 'cpi-info-open';
const SIZEGUIDE_CLASS = 'cpi-sizeguide-open';
const ZOOM_CLASS = 'cpi-zoom-open';

class CustomProductInformation extends HTMLElement {
  connectedCallback() {
    this.variants = this.#readVariants();
    /** @type {HTMLElement | null} */
    this.opener = null;

    // The `.cpi__dock` (gallery control + trigger) lives OUTSIDE this element
    // (as a sibling in the section wrapper) so it can span the full section
    // height. Scope click delegation + element lookups to that wrapper so the
    // trigger, size-guide and carousel thumbnails still work.
    this.scope = this.parentElement ?? document;

    this.scope.addEventListener('change', this.#onOptionChange);
    this.scope.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('cart-drawer:open', this.#onCartDrawerOpen);
    window.addEventListener('resize', this.#onResize);

    this.#initCarousel();
    this.#syncSelection();

    // Position the (now decoupled) gallery control relative to the trigger card.
    this.#positionGalleryDock();
    requestAnimationFrame(() => this.#positionGalleryDock());
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.#positionGalleryDock(), { once: true });
    }
  }

  disconnectedCallback() {
    this.scope?.removeEventListener('change', this.#onOptionChange);
    this.scope?.removeEventListener('click', this.#onClick);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('cart-drawer:open', this.#onCartDrawerOpen);
    window.removeEventListener('resize', this.#onResize);
    document.body.classList.remove(BODY_CLASS);
    document.body.classList.remove(SIZEGUIDE_CLASS);
    document.body.classList.remove(ZOOM_CLASS);
  }

  /**
   * Query within the section scope (covers this element AND the external dock).
   * @param {string} selector
   */
  #find(selector) {
    return this.scope?.querySelector(selector) ?? this.querySelector(selector);
  }

  #readVariants() {
    const el = this.querySelector('[data-cpi-variants]');
    try {
      return JSON.parse(el?.textContent || '[]');
    } catch {
      return [];
    }
  }

  /* ---------------- Drawer ---------------- */

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-cpi-open]')) {
      this.opener = target.closest('[data-cpi-open]');
      this.openInfo();
    } else if (target.closest('[data-cpi-size-guide]')) {
      this.sizeGuideOpener = target.closest('[data-cpi-size-guide]');
      this.openSizeGuide();
    } else if (target.closest('[data-cpi-sizeguide-back]')) {
      this.closeSizeGuide();
    } else if (target.closest('[data-cpi-sizeguide-close]')) {
      this.closeSizeGuide({ closeInfo: true });
    } else if (target.closest('[data-cpi-unit]')) {
      this.#setUnit(target.closest('[data-cpi-unit]').dataset.cpiUnit);
    } else if (target.closest('[data-cpi-scroll-dir]')) {
      this.#scrollTable(target.closest('[data-cpi-scroll-dir]'));
    } else if (target.closest('.cpi-band__description-toggle')) {
      event.preventDefault();
      this.#toggleDescription();
    } else if (target.closest('[data-cpi-close]')) {
      this.closeInfo();
    } else if (target.closest('[data-cpi-zoom-close]')) {
      this.closeZoomModal();
    } else if (target.closest('[data-cpi-media] .swiper-zoom-container')) {
      // Clicking the main gallery image opens the zoom modal instead of
      // zooming in place — the modal owns all zoom interaction now.
      const mainEl = this.querySelector('[data-cpi-media]');
      if (mainEl instanceof HTMLElement && mainEl.dataset.enableZoom === 'true') {
        this.openZoomModal(this.mainSwiper?.activeIndex ?? 0);
      }
    }
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      if (document.body.classList.contains(ZOOM_CLASS)) this.closeZoomModal();
      else if (document.body.classList.contains(SIZEGUIDE_CLASS)) this.closeSizeGuide();
      else if (document.body.classList.contains(BODY_CLASS)) this.closeInfo();
      return;
    }

    if (event.key === 'Tab') {
      if (document.body.classList.contains(ZOOM_CLASS)) {
        this.#trapFocus(this.querySelector('[data-cpi-zoom]'), event);
      } else if (document.body.classList.contains(SIZEGUIDE_CLASS)) {
        this.#trapFocus(this.querySelector('.cpi-sizeguide__panel'), event);
      } else if (document.body.classList.contains(BODY_CLASS)) {
        this.#trapFocus(this.querySelector('.cpi__panel'), event);
      }
    }
  };

  /**
   * Keeps Tab focus inside the given container (focus trap).
   * @param {Element | null} container
   * @param {KeyboardEvent} event
   */
  #trapFocus(container, event) {
    if (!(container instanceof HTMLElement)) return;
    const focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const visible = Array.from(focusables).filter((el) => el instanceof HTMLElement && el.offsetParent !== null);
    if (visible.length === 0) return;

    const first = visible[0];
    const last = visible[visible.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  openInfo() {
    document.body.classList.add(BODY_CLASS);
    const info = this.querySelector('[data-cpi-info]');
    info?.setAttribute('aria-hidden', 'false');
    // Move focus into the panel.
    const focusable = info?.querySelector('button, [href], input, select, [tabindex]:not([tabindex="-1"])');
    if (focusable instanceof HTMLElement) focusable.focus();
  }

  closeInfo() {
    document.body.classList.remove(BODY_CLASS);
    this.querySelector('[data-cpi-info]')?.setAttribute('aria-hidden', 'true');
    if (this.opener instanceof HTMLElement) {
      this.opener.focus();
      this.opener = null;
    }
  }

  #onCartDrawerOpen = () => {
    if (document.body.classList.contains(SIZEGUIDE_CLASS)) this.closeSizeGuide({ closeInfo: true });
    else if (document.body.classList.contains(BODY_CLASS)) this.closeInfo();
  };

  /* ---------------- Size guide ---------------- */

  openSizeGuide() {
    document.body.classList.add(SIZEGUIDE_CLASS);
    const panel = this.querySelector('[data-cpi-sizeguide]');
    panel?.setAttribute('aria-hidden', 'false');
    const focusable = panel?.querySelector('button, [href], [tabindex]:not([tabindex="-1"])');
    if (focusable instanceof HTMLElement) focusable.focus();
    // Tables need layout before we can measure overflow — wait a frame, then
    // re-check once more in case a late web-font load shifts column widths.
    requestAnimationFrame(() => this.#initTableScrollers());
    setTimeout(() => this.#initTableScrollers(), 300);
  }

  /* ---------------- Size-guide table scrollers ---------------- */

  #onResize = () => {
    if (document.body.classList.contains(SIZEGUIDE_CLASS)) this.#initTableScrollers();
    this.#positionGalleryDock();
  };

  /**
   * Sets --cpi-gallery-bottom so the sticky gallery pill rests 16px above the
   * trigger card. The card is sticky 40px above the viewport bottom; the pill is
   * inset 24px inside its zero-height dock, so the dock's sticky offset works out
   * to cardHeight + 40 + 16 - 24 = cardHeight + 32. Re-run on resize because the
   * card height changes with the viewport (e.g. title wrapping).
   */
  #positionGalleryDock() {
    const wrapper = this.#find('.cpi__sticky-wrapper');
    const dock = this.#find('[data-cpi-gallery-dock]');
    if (!(wrapper instanceof HTMLElement) || !(dock instanceof HTMLElement)) return;
    this.style.setProperty('--cpi-gallery-bottom', `${wrapper.offsetHeight + 32}px`);
  }

  /**
   * Reveal the ← / track / → control on any size-guide table that overflows its
   * container, wire its viewport's scroll listener once, and sync the control state.
   */
  #initTableScrollers() {
    for (const scroller of this.querySelectorAll('[data-cpi-scroller]')) {
      const viewport = scroller.querySelector('[data-cpi-scroll-viewport]');
      const nav = scroller.querySelector('[data-cpi-scroll-nav]');
      if (!(viewport instanceof HTMLElement) || !(nav instanceof HTMLElement)) continue;

      const overflows = viewport.scrollWidth - viewport.clientWidth > 1;
      nav.hidden = !overflows;
      if (!overflows) continue;

      if (!viewport.dataset.cpiScrollBound) {
        viewport.dataset.cpiScrollBound = 'true';
        viewport.addEventListener('scroll', () => this.#updateScroller(scroller), { passive: true });
      }
      this.#updateScroller(scroller);
    }
  }

  /** Scrolls a table by ~80% of its visible width in the arrow's direction. */
  #scrollTable(button) {
    const scroller = button.closest('[data-cpi-scroller]');
    const viewport = scroller?.querySelector('[data-cpi-scroll-viewport]');
    if (!(viewport instanceof HTMLElement)) return;
    const dir = Number(button.dataset.cpiScrollDir) || 1;
    // "Next" content lies to the left in RTL.
    const sign = getComputedStyle(viewport).direction === 'rtl' ? -1 : 1;
    viewport.scrollBy({ left: sign * dir * viewport.clientWidth * 0.8, behavior: 'smooth' });
  }

  /** Syncs the thumb position/width and arrow disabled states to the scroll offset. */
  #updateScroller(scroller) {
    const viewport = scroller.querySelector('[data-cpi-scroll-viewport]');
    const thumb = scroller.querySelector('[data-cpi-scroll-thumb]');
    const prev = scroller.querySelector('[data-cpi-scroll-dir="-1"]');
    const next = scroller.querySelector('[data-cpi-scroll-dir="1"]');
    if (!(viewport instanceof HTMLElement)) return;

    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    // Clamp: on mobile, rubber-band / momentum over-scroll can push scrollLeft outside
    // [0, maxScroll], which would slide the thumb past the track edge (and over the arrow).
    // scrollLeft runs negative in RTL, so measure distance from the start edge.
    const clampedLeft = Math.min(Math.max(Math.abs(viewport.scrollLeft), 0), maxScroll);
    const ratio = maxScroll > 0 ? clampedLeft / maxScroll : 0;

    if (thumb instanceof HTMLElement) {
      const widthPct = Math.min(100, Math.max(15, (viewport.clientWidth / viewport.scrollWidth) * 100));
      thumb.style.width = `${widthPct}%`;
      thumb.style.insetInlineStart = `${ratio * (100 - widthPct)}%`;
    }
    if (prev instanceof HTMLButtonElement) prev.disabled = clampedLeft <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = clampedLeft >= maxScroll - 1;
  }

  /**
   * @param {{ closeInfo?: boolean }} [options] - Also close the info panel (the X button).
   */
  closeSizeGuide(options = {}) {
    document.body.classList.remove(SIZEGUIDE_CLASS);
    this.querySelector('[data-cpi-sizeguide]')?.setAttribute('aria-hidden', 'true');

    if (options.closeInfo) {
      this.closeInfo();
    } else if (this.sizeGuideOpener instanceof HTMLElement) {
      // Return focus to the "Size Guide" trigger in the info panel.
      this.sizeGuideOpener.focus();
    }
    this.sizeGuideOpener = null;
  }

  /* ---------------- Zoom modal ---------------- */

  /**
   * Opens the full-screen zoom modal, synced to the given media index.
   * @param {number} index - The slide index to open on (matches the main gallery's).
   */
  openZoomModal(index) {
    document.body.classList.add(ZOOM_CLASS);
    const modal = this.querySelector('[data-cpi-zoom]');
    modal?.setAttribute('aria-hidden', 'false');

    if (this.zoomSwiper && typeof index === 'number') this.zoomSwiper.slideTo(index, 0);
    this.#handleReducedMotionVideo(this.zoomSwiper);

    const closeButton = modal?.querySelector('[data-cpi-zoom-close]');
    if (closeButton instanceof HTMLElement) closeButton.focus();
  }

  closeZoomModal() {
    document.body.classList.remove(ZOOM_CLASS);
    this.querySelector('[data-cpi-zoom]')?.setAttribute('aria-hidden', 'true');

    // Pause the modal's videos — they're hidden but would otherwise keep playing.
    for (const video of this.querySelectorAll('[data-cpi-zoom] [data-cpi-video]')) {
      if (video instanceof HTMLVideoElement) video.pause();
    }

    // The opener was a plain <img>, not a focusable control — return focus to the
    // main gallery itself (tabindex="-1" in the markup makes it a valid target).
    const mainEl = this.querySelector('[data-cpi-media]');
    if (mainEl instanceof HTMLElement) mainEl.focus();
  }

  /**
   * Switches the measurement table between cm and inches in place.
   * @param {string} unit - "cm" or "in".
   */
  #setUnit(unit) {
    if (unit !== 'cm' && unit !== 'in') return;

    for (const button of this.querySelectorAll('[data-cpi-unit]')) {
      const active = button.dataset.cpiUnit === unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    const units = this.querySelector('[data-cpi-sizeguide]')?.dataset ?? {};
    for (const label of this.querySelectorAll('[data-cpi-unit-label]')) {
      label.textContent = unit === 'in' ? `(${units.unitIn || 'in'})` : `(${units.unitCm || 'cm'})`;
    }

    for (const cell of this.querySelectorAll('[data-cpi-cm]')) {
      const cm = parseFloat(cell.dataset.cpiCm);
      if (Number.isNaN(cm)) continue;
      cell.textContent = unit === 'in' ? (cm / 2.54).toFixed(1) : String(cm);
    }
  }

  /* ---------------- Description read more/less ---------------- */

  /**
   * Expands/collapses the product description band by toggling `.expanded`
   * on `.cpi-band__description` (the CSS animates its max-height). The band
   * lives outside this element, so lookups go through the section scope.
   */
  #toggleDescription() {
    const description = this.#find('.cpi-band__description');
    if (!(description instanceof HTMLElement)) return;

    const expanded = description.classList.toggle('expanded');

    const link = this.#find('.cpi-band__description-toggle-link');
    if (link instanceof HTMLElement) {
      link.textContent = expanded
        ? this.dataset.readLessText || 'Read less'
        : this.dataset.readMoreText || 'Read more';
      link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  /* ---------------- Variant selection ---------------- */

  #onOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.hasAttribute('data-cpi-option-input')) {
      this.#syncSelection();
      if (target.closest('.cpi__option--colour') && this.#missingChoices().length > 0) {
        const mediaId = this.#colourPreviewMediaId();
        if (mediaId) this.#goToMedia(String(mediaId));
      }
    } else if (target.hasAttribute('data-cpi-bodyscan-toggle')) {
      this.dataset.bodyscan = /** @type {HTMLInputElement} */ (target).checked ? 'on' : 'off';
      this.#syncSelection();
    }
  };

  get #selectedOptions() {
    return Array.from(this.querySelectorAll('[data-cpi-option-input]:checked'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);
  }

  #matchVariant() {
    if (this.#missingChoices().length > 0) return undefined;
    const selected = this.#selectedOptions;
    if (selected.length === 0) return this.variants[0];
    return this.variants.find(
      (variant) =>
        Array.isArray(variant.options) &&
        variant.options.length === selected.length &&
        variant.options.every((value, index) => value === selected[index])
    );
  }

  #syncSelection() {
    const variant = this.#matchVariant();
    if (!variant) {
      this.#showChoiceNeeded();
      return;
    }

    const idInput = this.querySelector('[data-cpi-variant-id]');
    if (idInput instanceof HTMLInputElement) idInput.value = variant.id;

    const priceEl = this.#find('[data-cpi-price]');
    if (priceEl && variant.price_formatted) priceEl.textContent = variant.price_formatted;

    this.#updateAddButton(variant);
    this.#syncColourPills();
    if (variant.media_id) this.#goToMedia(String(variant.media_id));
  }

  #syncColourPills() {
    const colourInputs = this.querySelectorAll('.cpi__option--colour [data-cpi-option-input]');
    if (colourInputs.length === 0) return;

    const selected = Array.from(this.querySelectorAll('[data-cpi-option-input]:checked'))
      .map((input) => ({ position: Number(input.dataset.optionPosition), value: input.value }))
      .sort((a, b) => a.position - b.position);

    for (const input of colourInputs) {
      const position = Number(input.dataset.optionPosition);
      const candidate = selected.map((entry) => (entry.position === position ? input.value : entry.value));
      const resolvedVariant = this.variants.find(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === candidate.length &&
          variant.options.every((value, index) => value === candidate[index])
      );

      const pill = input.nextElementSibling;
      if (!(pill instanceof HTMLElement)) continue;

      const hex = (resolvedVariant && resolvedVariant.colour_hex) || '#453128';
      pill.style.setProperty('--pill-bg', hex);
      pill.style.setProperty('--pill-fg', this.#contrastColour(hex));
    }
  }

  #contrastColour(hex) {
    const match = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!match) return '#faf6e8';

    const r = parseInt(match[1].slice(0, 2), 16);
    const g = parseInt(match[1].slice(2, 4), 16);
    const b = parseInt(match[1].slice(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 150 ? '#1c1917' : '#faf6e8';
  }

  #updateAddButton(variant) {
    const button = this.querySelector('.cpi__add');
    if (!(button instanceof HTMLButtonElement)) return;

    this.removeAttribute('data-selection-incomplete');
    const label = this.#addButtonLabel(button);
    const fit = this.#selectedFitLabel();

    const addText = this.dataset.addText || 'Add to Bag';

    if (!variant.available) {
      button.disabled = true;
      if (label) label.textContent = this.dataset.soldOutText || 'Sold out';
      return;
    }

    button.disabled = false;
    if (label) {
      if (this.dataset.bodyscan === 'on') {
        label.textContent = `${this.dataset.bodyscanSizeText || 'Your Bodyscan Size'} - ${addText}`;
      } else {
        label.textContent = fit ? `${fit} - ${addText}` : addText;
      }
    }
  }

  #addButtonLabel(button) {
    return button.querySelector('.add-to-cart-text__content span > span') || button.querySelector('.add-to-cart-text__content');
  }

  #missingChoices() {
    return ['size', 'height'].filter((role) =>
      Array.from(this.querySelectorAll(`.cpi__option--${role}`)).some(
        (field) => !field.querySelector('[data-cpi-option-input]:checked')
      )
    );
  }

  #showChoiceNeeded() {
    const idInput = this.querySelector('[data-cpi-variant-id]');
    if (idInput instanceof HTMLInputElement) idInput.value = '';

    const button = this.querySelector('.cpi__add');
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = true;

    const missing = this.#missingChoices();
    const anyAvailable = this.variants.some((variant) => variant.available);
    const prompting = missing.length > 0 && anyAvailable;
    this.toggleAttribute('data-selection-incomplete', prompting);

    const label = this.#addButtonLabel(button);
    if (!label) return;
    if (prompting) {
      label.textContent = this.#choicePrompt(missing);
    } else if (!anyAvailable) {
      label.textContent = this.dataset.soldOutText || 'Sold out';
    } else {
      label.textContent = this.dataset.unavailableText || 'Unavailable';
    }
  }

  #choicePrompt(missing) {
    if (missing.length > 1) return this.dataset.selectSizeHeightText || 'Select size and height';
    if (missing[0] === 'height') return this.dataset.selectHeightText || 'Select height';
    return this.dataset.selectSizeText || 'Select size';
  }

  #selectedFitLabel() {
    return ['size', 'height']
      .flatMap((role) => Array.from(this.querySelectorAll(`.cpi__option--${role} [data-cpi-option-input]:checked`)))
      .map((input) => (input instanceof HTMLInputElement ? input.value : ''))
      .filter(Boolean)
      .join(' / ');
  }

  #colourPreviewMediaId() {
    const colour = this.querySelector('.cpi__option--colour [data-cpi-option-input]:checked');
    if (!(colour instanceof HTMLInputElement)) return null;

    const index = Number(colour.dataset.optionPosition) - 1;
    const match = this.variants.find(
      (variant) => variant.media_id && Array.isArray(variant.options) && variant.options[index] === colour.value
    );
    return match ? match.media_id : null;
  }

  /* ---------------- Media (Swiper) ---------------- */

  #initCarousel() {
    // Wait for the (deferred) Swiper bundle to be available, then build the
    // carousel. Polls briefly and also falls back to window `load`.
    const start = Date.now();
    const waitForSwiper = () => {
      if (window.Swiper) {
        init();
      } else if (Date.now() - start < 8000) {
        requestAnimationFrame(waitForSwiper);
      } else {
        window.addEventListener('load', init, { once: true });
      }
    };

    const init = () => {
      const SwiperCtor = window.Swiper;
      const mainEl = this.querySelector('[data-cpi-media]');
      if (!SwiperCtor || !(mainEl instanceof HTMLElement) || mainEl.dataset.swiperInit) return;

      const thumbsEl = this.#find('[data-cpi-thumbs]');
      let thumbs = null;
      if (thumbsEl instanceof HTMLElement) {
        thumbs = new SwiperCtor(thumbsEl, {
          slidesPerView: 'auto',
          spaceBetween: 4,
          watchSlidesProgress: true,
          slideToClickedSlide: true,
        });
      }

      // Media treatment (resolved in Liquid from the first media's aspect ratio):
      //   portrait_side_by_side .. two images per slide on desktop, one on mobile.
      //   landscape_lead ......... wide first image leads as a full-width slide,
      //                            the rest pair up two-per-slide on desktop; one
      //                            per slide on mobile.
      //   full_bleed_landscape ... one full-width image per slide everywhere.
      const treatment = mainEl.dataset.treatment;
      const enableZoom = mainEl.dataset.enableZoom === 'true';

      /** @type {Record<string, unknown>} */
      const config = {
        slidesPerView: 1,
        slidesPerGroup: 1,
        speed: 400,
        watchSlidesProgress: true,
        thumbs: thumbs ? { swiper: thumbs, multipleActiveThumbs: true } : undefined,
        navigation: {
          prevEl: this.#find('[data-cpi-prev]'),
          nextEl: this.#find('[data-cpi-next]'),
          disabledClass: 'swiper-button-disabled',
        },
        // Zoom now lives entirely in the zoom modal (see #initZoomModalCarousel) —
        // clicking the main gallery opens that modal instead of zooming in place,
        // so the zoom module (which also adds pinch-to-zoom/double-tap) is off here.
        zoom: false,
        on: {
          slideChange: (swiper) => {
            this.#handleReducedMotionVideo(swiper);
            this.#updateSlideCounter(swiper);
          },
          slideChangeTransitionEnd: (swiper) => this.#handleReducedMotionVideo(swiper),
        },
      };

      // Desktop-only pairing (mobile stays one-per-slide via the base config above).
      if (treatment === 'portrait_side_by_side') {
        // Every slide pairs up two-per-slide.
        config.breakpoints = {
          750: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
        };
      } else if (treatment === 'landscape_lead') {
        // Wide first image full-width, the rest paired. `slidesPerView: 'auto'`
        // reads the per-slide widths from CSS (100% first, 50% after);
        // slidesPerGroupSkip keeps the lead slide as its own page so navigation
        // snaps [1] -> [2,3] -> [4,5] ... instead of drifting through overlapping
        // pairs.
        config.breakpoints = {
          750: {
            slidesPerView: 'auto',
            slidesPerGroup: 2,
            slidesPerGroupSkip: 1,
          },
        };
      }

      this.mainSwiper = new SwiperCtor(mainEl, config);

      this.#handleReducedMotionVideo(this.mainSwiper);
      this.#updateSlideCounter(this.mainSwiper);
      mainEl.dataset.swiperInit = 'true';

      this.#initZoomModalCarousel(SwiperCtor, enableZoom);
    };

    waitForSwiper();
  }

  /**
   * Builds the zoom modal's own Swiper instance — a separate carousel (not the main
   * gallery's) so it can zoom independently. Wires click-to-toggle zoom the same way
   * the main gallery used to before that moved here (see #initCarousel).
   * @param {any} SwiperCtor
   * @param {boolean} enableZoom
   */
  #initZoomModalCarousel(SwiperCtor, enableZoom) {
    const zoomEl = this.querySelector('[data-cpi-zoom-media]');
    if (!(zoomEl instanceof HTMLElement) || zoomEl.dataset.swiperInit) return;

    this.zoomSwiper = new SwiperCtor(zoomEl, {
      slidesPerView: 1,
      slidesPerGroup: 1,
      speed: 400,
      watchSlidesProgress: true,
      navigation: {
        prevEl: this.querySelector('[data-cpi-zoom-prev]'),
        nextEl: this.querySelector('[data-cpi-zoom-next]'),
        disabledClass: 'swiper-button-disabled',
      },
      zoom: enableZoom ? { maxRatio: 2.4 } : false,
      on: {
        slideChange: (swiper) => this.#handleReducedMotionVideo(swiper),
        slideChangeTransitionEnd: (swiper) => this.#handleReducedMotionVideo(swiper),
      },
    });

    if (enableZoom) {
      zoomEl.addEventListener('click', (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('.swiper-zoom-container')) return;
        const zoom = this.zoomSwiper?.zoom;
        if (!zoom) return;
        if (zoom.scale > 1) zoom.out();
        else zoom.in();
      });
    }

    zoomEl.dataset.swiperInit = 'true';
  }

  /**
   * Plays the video(s) on the active/visible slide(s) of the given carousel and
   * pauses the rest. Videos on slides that start off-screen never fire their
   * `autoplay` attribute, so this kicks them off once their slide becomes active.
   * Honors prefers-reduced-motion (posters only).
   * @param {any} swiper
   */
  #handleReducedMotionVideo(swiper) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Scope to this carousel's own slides so we don't touch the other
    // (main vs. zoom) gallery's videos. Fall back to every video when we don't
    // have a swiper instance yet.
    const slides = swiper?.slides ? Array.from(swiper.slides) : null;
    const videos = slides
      ? slides.flatMap((slide) => Array.from(slide.querySelectorAll('[data-cpi-video]')))
      : Array.from(this.querySelectorAll('[data-cpi-video]'));

    for (const video of videos) {
      if (!(video instanceof HTMLVideoElement)) continue;

      if (reduce) {
        video.removeAttribute('autoplay');
        video.pause();
        continue;
      }

      const slide = video.closest('.swiper-slide');
      // `swiper-slide-visible` (from watchSlidesProgress) covers slidesPerView > 1;
      // `swiper-slide-active` covers the single-per-view case. No slide = play.
      const live = slide
        ? slide.classList.contains('swiper-slide-visible') || slide.classList.contains('swiper-slide-active')
        : true;

      if (live) {
        if (video.paused) {
          // Autoplay requires muted; enforce it in case the attribute was stripped.
          video.muted = true;
          const play = video.play();
          if (play && typeof play.catch === 'function') play.catch(() => {});
        }
      } else if (!video.paused) {
        video.pause();
      }
    }
  }

  /**
   * Updates the "current / total" slide counter in the dock for the main gallery.
   * The counter markup lives outside this element, so lookups use the section scope.
   * @param {any} swiper
   */
  #updateSlideCounter(swiper) {
    if (!swiper) return;

    const totalSlides = swiper.slides?.length ?? 0;
    const activeIndex = swiper.activeIndex ?? 0;
    const perGroup = Number(swiper.params?.slidesPerGroup) || 1;
    const perView = swiper.params?.slidesPerView;
    // Side-by-side (slidesPerView > 1) counts pages, not individual slides.
    const paged = typeof perView === 'number' && perView > 1;

    const currentValue = paged ? Math.floor(activeIndex / perGroup) + 1 : activeIndex + 1;
    const totalValue = paged ? Math.ceil(totalSlides / perGroup) : totalSlides;

    const current = this.#find('.cpi__media-currentslide');
    if (current instanceof HTMLElement) current.textContent = String(currentValue);

    const total = this.#find('.cpi__media-total-slides');
    if (total instanceof HTMLElement) total.textContent = String(totalValue);
  }

  #goToMedia(mediaId) {
    if (!mediaId || !this.mainSwiper) return;
    const slides = Array.from(this.querySelectorAll('[data-cpi-media] .swiper-slide'));
    const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.mainSwiper.slideTo(index);
  }
}

if (!customElements.get('custom-product-information')) {
  customElements.define('custom-product-information', CustomProductInformation);
}
